import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RecoveryErrorSolutionName } from 'mbs-ui-shared@models/recovery-error-solution-name.enum';
import { untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, noop, Observable, Subject } from 'rxjs';
import { filter, first, timeout } from 'rxjs/operators';
import type { RecoverySolutionActivationData } from 'mbs-ui-shared@models/recovery-solution-activation-data.interface';
import { RecoverySolutionExecuteStatus } from 'mbs-ui-shared@models/recovery-solution-execute-status.enum';
import type { RecoverySolutionExecutionData } from 'mbs-ui-shared@models/recovery-solution-execution-data.interface';
import { OmitRSEData } from 'mbs-ui-shared@models/recovery-solution-execution-data.interface';

/**
 * @interface RecoverySolutionExecutor - contract for typical Error Solution Executor
 */
export interface RecoverySolutionExecutor {
  recoveryStatusService: RecoveryStatusService;
  canExecuteSolution: (data: OmitRSEData) => boolean;
  executeSolution: (data: OmitRSEData) => Observable<boolean>;
}

export type ExecuteSolutionsCommandParams = {
  agentType: 'backup';
  commandType: 'ExecuteSolution';
  params: {
    solutionType: RecoveryErrorSolutionName;
    planId?: string;
    parameters?: { parameterName: string };
  };
};

export type ExecuteSolutionsCommandResponse = { data?: { result: boolean; message?: string }, result: boolean; message?: string, title?: string };

/**
 * Helper class
 * Will subscribe to RecoveryStatusService and call additional checks before execute solution
 */
class RecoverySolutionSubscriber {
  constructor(control: RecoverySolutionExecutor) {
    control.recoveryStatusService.solution.pipe(untilDestroyed(control), filter(Boolean)).subscribe((data) => {
      if (data.status === RecoverySolutionExecuteStatus.new && control.canExecuteSolution(data)) {
        data.status = RecoverySolutionExecuteStatus.inProgress;
        data.executionProgress = control.executeSolution(data);
      }
    });
  }
}

@Injectable({ providedIn: 'root' })
export class RecoveryStatusService {
  public get solution(): Observable<RecoverySolutionExecutionData> {
    return this.activeSolution.asObservable();
  }

  public get currentActiveSolution(): RecoverySolutionExecutionData {
    return { ...this.activeSolution?.value };
  }

  private timeout = 50 * 60 * 1000; // 50 minutes
  private activeSolution: BehaviorSubject<RecoverySolutionExecutionData> = new BehaviorSubject<RecoverySolutionExecutionData>(null);

  constructor(private http: HttpClient) {}

  executeSolutionsCommand(hid: string, payload: ExecuteSolutionsCommandParams): Observable<ExecuteSolutionsCommandResponse> {
    return this.http.post<ExecuteSolutionsCommandResponse>(`api/computers/${hid}/remotecommand`, payload);
  }

  /**
   * Call service to find all possible executors for solution and execute it
   * @param {RecoverySolutionActivationData} data - meta info about error and solution
   * @return {RecoverySolutionActivationData} the same meta info, but with activationProgress Observable
   */
  activateSolution(data: Omit<RecoverySolutionActivationData, 'activationProgress'>): RecoverySolutionActivationData {
    return { ...data, activationProgress: this.executeSolution(data) };
  }

  private executeSolution(data: Omit<RecoverySolutionActivationData, 'activationProgress'>): Observable<boolean> {
    const result = new Subject<boolean>();

    queueMicrotask(() => {
      const newData: RecoverySolutionExecutionData = {
        additionalInfo: { ...data.additionalInfo },
        solution: { ...data.solution },
        status: RecoverySolutionExecuteStatus.new
      };

      this.activeSolution.next(newData);

      if (newData.status === RecoverySolutionExecuteStatus.new || newData.status === RecoverySolutionExecuteStatus.fail) {
        return void result.next(false);
      }

      newData.executionProgress.pipe(timeout(this.timeout), first()).subscribe({
        next: (res: boolean) => result.next(res),
        error: () => result.next(false),
        complete: noop
      });
    });

    return result.asObservable();
  }

  static RecoverySolutionSubscriber = RecoverySolutionSubscriber;
}
